<script
  setup
  lang="ts"
>
import {legacyModeKey, LegacyOrder, useHost, useLocalization, useProcesses} from '@hantera/portal-app';
import {Panel, TextInput} from '@hantera/design-system';
import {computed, inject, reactive, ref, watch} from 'vue';
import useBackend from '@/composition/useBackend';
import {SearchResult} from '@/backend/search';
import useWorkspace from '@/composition/useWorkspace';
import LegacyCompactOrder from '@/components/LegacyCompactOrder';
import cache from '@/backend/cache';
import {TabView} from "@/view-managers/workspace/workspace-types";
import {v7} from "uuid";

const localStorageKey = 'quick-search_state';

defineExpose({
  focus() {
    if (input.value) {
      input.value.focus();
    }
  }
});

const legacyMode = inject(legacyModeKey, false);

const input = ref<InstanceType<typeof TextInput>>()
const { search, legacyOrders: orders } = useBackend();
const t = useLocalization();
const phrase = ref('');
const serverResponse = ref<SearchResult>({ orders: [] });
const workspace = useWorkspace();
const currentIdx = ref(-1);
const host = useHost();
const processes = useProcesses()

const recentState = reactive<{
  type: string
  id: string
}[]>((JSON.parse(localStorage.getItem(localStorageKey) || '[]') as any));
const recentHits = ref<Hit[]>([]);

watch(recentState, async () => {
  localStorage.setItem(localStorageKey, JSON.stringify(recentState));

  let i = 0;
  const result: Hit[] = [];
  var toRemove = [];
  for (const r of recentState) {
    if (r.type === 'order') {

      let order = cache.legacyOrders.get(r.id);
      if (!order) {
        order = await orders.get(r.id);
      }

      if (order) {
        result.push(orderToHit(order, i++));
      } else {
        toRemove.push(r);
      }
    }
  };
  recentHits.value = result.sort((a, b) => b.sort - a.sort);

  if (toRemove.length > 0) {
    for (const t of toRemove) {
      recentState.splice(recentState.findIndex(r => r.id === t.id && r.type === t.type));
    }
    localStorage.setItem(localStorageKey, JSON.stringify(recentState));
  }

}, { immediate: true });

let timeout: ReturnType<typeof setTimeout> | undefined;
let searchController: AbortController | undefined;
watch(phrase, () => {
  performSearch();
});

function performSearch(debounceDelay: number = 200) {
  if (timeout) {
    clearTimeout(timeout);
  }
  if (searchController) {
    searchController.abort();
    searchController = undefined;
  }
  timeout = setTimeout(async () => {
    if (!phrase.value || phrase.value.trim() === '') {
      clear();
      return;
    }
    searchController = new AbortController();
    try {
      serverResponse.value = await search.search(phrase.value, searchController.signal);
    }
    catch (err: any) {
      if (err.name !== 'AbortError') {
        processes.error({ message: 'Search failed: ' + err });
      }
    }
  }, debounceDelay);
}

function clear() {
  phrase.value = '';
  currentIdx.value = -1;
  serverResponse.value = {
    orders: [],
  };
}

interface Hit {
  key: string
  type: string
  open: () => void
  sort: number
  args: any
  component: any
}

function addRecent(type: string, id: string) {
  const existingIdx = recentState.findIndex(r => r.type === type && r.id === id);
  if (existingIdx >= 0) {
    recentState.splice(existingIdx, 1);
  }
  recentState.push({
    type,
    id
  });
  if (recentState.length > 10) {
    recentState.splice(0, 1);
  }
}

function orderToHit(order: LegacyOrder, sort: number) {
  return {
    key: `order-${order.orderId}`,
    sort: sort,
    type: 'order',
    open: () => {
      if (!workspace.pane) { return; }
      close();
      addRecent('order', order.orderId);

      workspace.openTab(
        workspace.pane.paneId,
        <TabView>{
          type: 'view',
          tabId: v7(),
          path: legacyMode ? '/legacy-order' : '/order',
          query: {
            id: order.orderId,
          },
          state: {},
          count: 0,
        }
      )
      
      clear();
    },
    args: {
      order: order,
    },
    component: LegacyCompactOrder,
  };
}

const hits = computed(() => {
  const result: Hit[] = [];
  serverResponse.value.orders.forEach((o: any) => {
    const hit = orderToHit(o, o.createdUtc.getTime());
    const recentIdx = recentState.findIndex(r => r.type === 'order' && r.id === o.orderId);
    if (recentIdx >= 0) {
      hit.sort = recentIdx * -9999;
    }
    result.push(hit);
  });

  result.sort((a, b) => a.sort - b.sort);

  return result;
});

function keydown(e: KeyboardEvent) {
  if (e.key === 'ArrowUp') {
    currentIdx.value = Math.max(-1, currentIdx.value - 1);
  } else if (e.key === 'ArrowDown') {
    if (phrase.value.length === 0) {
      currentIdx.value = Math.min(recentHits.value.length - 1, currentIdx.value + 1);
    } else {
      currentIdx.value = Math.min(hits.value.length - 1, currentIdx.value + 1);
    }
  } else if (e.key === 'Escape') {
    clear();
  } else if (e.key === 'Enter') {
    if (currentIdx.value < 0) {
      return;
    }

    if (phrase.value.length === 0) {
      recentHits.value[currentIdx.value].open();
    } else {
      hits.value[currentIdx.value].open();
    }
  }
}

</script>

<template>
  <div
    class="quick-search"
    @click.stop
  >
    <TextInput
      :placeholder="t('workspace.quickSearchPlaceholder')"
      ref="input"
      v-model="phrase"
      @keydown="keydown"
    />
    <div class="quick-search__bottom">
      <Panel
        class="quick-search__recent"
        v-if="phrase.length === 0 && recentHits.length > 0"
        @mouseout="currentIdx = -1"
      >
        <div
          v-for="(hit, idx) in recentHits"
          :key="hit.key"
          class="quick-search__result-row"
          :class="{
            'quick-search__result-row--selected': currentIdx === idx
          }"
          @click="hit.open"
          @mouseover="currentIdx = idx"
        >
          <div class="quick-search__result-type">{{ hit.type }}</div>
          <div class="quick-search__result-item">
            <component
              :is="hit.component"
              v-bind="hit.args"
            />
          </div>
        </div>
      </Panel>
      <Panel
        class="quick-search__results"
        v-if="phrase.length > 0 && (hits.length > 0)"
      >
        <div
          v-for="(hit, idx) in hits"
          :key="hit.key"
          class="quick-search__result-row"
          :class="{
            'quick-search__result-row--selected': currentIdx === idx
          }"
          @click="hit.open"
          @mouseover="currentIdx = idx"
        >
          <div class="quick-search__result-type">{{ hit.type }}</div>
          <div class="quick-search__result-item">
            <component
              :is="hit.component"
              v-bind="hit.args"
            />
          </div>
        </div>
      </Panel>
    </div>
  </div>
</template>

<style>
.quick-search {
  position: relative;
}

.quick-search input {
  width: 100%;
  margin: 0;
}

.quick-search__bottom {
  position: relative;
}

.quick-search__results {
  position: absolute;
  left: 0;
  right: 0;
  display: grid;
  grid-template-columns: min-content auto;
  padding: 0;
  background-color: rgb(var(--color-base));
  z-index: 100;
}

.quick-search__recent {
  margin-top: 32px;
  display: grid;
  grid-template-columns: min-content auto;
  padding: 0;
  background-color: rgb(var(--color-base));
}

.quick-search__result-row {
  display: contents;
  cursor: pointer;
}

.quick-search__result-row--selected .quick-search__result-type,
.quick-search__result-row--selected .quick-search__result-item {
  background-color: hsl(var(--color-80));
}

.quick-search__result-type,
.quick-search__result-item {
  border-bottom: 1px solid hsl(var(--color-70));
}

.quick-search__result-row:last-child .quick-search__result-type,
.quick-search__result-row:last-child .quick-search__result-item {
  border-bottom: none;
}

.quick-search__result-type {
  padding: 0 8px;
  display: flex;
  align-items: center;
  border-right: 1px solid hsl(var(--color-60));
  font-family: 'Suisse Intl Mono', monospace;
  font-size: 12px;
  text-align: right;
}
</style>